import { sgVersion, log as commonLog, trace, error } from './helpers'

declare global {
  interface HTMLElement {
    attachedPlugins: {
      [key: string]: boolean
    }
  }
}

type HTMLOptions<T> = { [key in keyof T]: T[key] extends boolean ? true : string }

interface SGPluginDefinition {
  name: string,
  fullName: string,
  hasCSS?: boolean,
  reactive?: boolean,
  file?: string,
  cssFile?: string | string[],
  cssIsGlobal?: boolean,
  selector?: string,
  observerRun?: () => void,
  run?: () => void,
  shouldRun?: (element: HTMLElement) => boolean,
  shouldRunInObserver?: (element: HTMLElement) => boolean,
  attachOnce?: boolean,
  ignoreSchedule?: boolean,

  [key: string]: any
}

export type SGPluginContruct = typeof SGPlugin | SGPluginDefinition

export class SGPlugin {
  /**
   * Имя плагина
   */
  name: string = ''
  fullName?: string
  hasCSS?: boolean
  attached: boolean = false
  attachOnce: boolean = false
  scheduled: boolean = false
  firstRun: boolean = true
  ignoreSchedule: boolean = false
  mediaQuery?: string

  /**
   * Элементы, на которых будет вызван плагин
   */
  elements?: HTMLElement[]

  /**
   * Имя файла основного плагина, который нужно грузить
   */
  file?: string

  /**
   * Имя CSS-файла, который нужно грузить
   */
  cssFile?: string

  /**
   * Использовать глобальный CSS из ./css или локальный из директории плагина
   */
  cssIsGlobal: boolean = false

  /**
   * Нужно ли заново вызывать плагин, если на странице произошли изменения
   */
  reactive: boolean = false

  /**
   * Словарь алиасов для опций.
   * Ключ — опция приходящая из html (всегда lowercase)
   * Значение — название опции в плагине
   *
   * Например:
   * {
   *   'min-length': 'minLength',
   *   onchange: 'onChange'
   * }
   */
  optionAliases: { [key: string]: string } = {}

  /**
   * Селектор, по которому мы выбираем элементы
   * По умолчанию ищет все элементы с аттрибутом data-%name%, где name — имя плагина
   */
  selector?: string

  getSelector(): string {
    if (this.selector) {
      return this.selector
    }
    return `[data-${this.name}]`
  }

  get bundleName() {
    if (!this.file) {
      throw new Error('Укажи имя файла')
    }
    return `/dist/js/plugins/${this.file}.js?${sgVersion()}`
  }

  get cssName() {
    if (!this.file && !this.cssFile) {
      throw new Error('Укажи имя файла?')
    }

    let files: string | string[] = this.cssFile ? this.cssFile : this.file
    let dir = 'js/plugins'
    if (this.cssIsGlobal) {
      dir = 'css'
    }

    if (!Array.isArray(files)) {
      files = [files]
    }

    return (files as string[]).map(file => `/dist/${dir}/${file}.css?${sgVersion()}`)
  }

  scheduleRun = async (elements: HTMLElement[] = null) => {
    if (this.scheduled && !this.ignoreSchedule) {
      return
    }
    this.scheduled = true
    this.firstRun = false
    if (!elements) {
      this.log('scheduling run without elements')
    }
    this.elements = elements
    await this.run(this.elements)
    this.scheduled = false
  }

  /**
   * Тело плагина, тут мы выполняем всю работу
   */
  run = async (elements?: NodeListOf<HTMLElement> | HTMLElement[]): Promise<void> => {
    let LoadedModule: (elements: NodeListOf<HTMLElement> | HTMLElement[], setDetach?: (fn: () => void) => void) => void;
    try {
      LoadedModule = (await import(this.bundleName)).default
    } catch (err) {
      console.error(new Error('Failed to load plugin: ' + this.bundleName))
    }

    if (LoadedModule) {
      this.registerCss()
      if (!elements) {
        elements = this.elements
      }
      if (elements) {
        elements.forEach((ele: HTMLElement) => {
          if (!ele.attachedPlugins) {
            ele.attachedPlugins = {}
          }
          ele.attachedPlugins[this.name] = true
        })
      }
      LoadedModule(elements, this.setDetach)
    }
  }

  registerCss = () => {
    if (this.hasCSS) {
      this.cssName.forEach(cssName => {
        if (document.querySelector(`link[href="${cssName}"]`)) {
          return
        }
        const style = document.createElement('link')
        style.rel = 'stylesheet'
        style.href = cssName
        document.head.append(style)
      })
    }
  }

  filterElements = (elements: HTMLElement[]): HTMLElement[] => {
    if (this.attachOnce) {
      elements = elements.filter(element => {
        if (!element.attachedPlugins) {
          element.attachedPlugins = {}
        }
        return !element.attachedPlugins[this.name]
      })
    }
    return elements
  }

  observerRun = (elements: HTMLElement[]): void => {
    elements = this.filterElements(elements)
    if (elements.length > 0) {
      // noinspection JSIgnoredPromiseFromCall
      this.scheduleRun(elements)
    }
  }

  /**
   * Функция, вызываемая при отключении плагина
   */
  detach = () => {
  }

  setDetach = (fn: () => void) => {
    this.detach = fn
  }

  /**
   * Хелпер для красивого логирования в консоль
   */
  log(...args: any[]) {
    commonLog(this.fullName || this.name, ...args)
  }

  /**
   * Хелпер, достающий из указанного элемента
   * значения всех аттрибутов начинающихся с data-%prefix%
   * Если removeData = true — удаляем эти аттрибуты из кода
   * @param element Элемент из которого берём параметры
   * @param prefix Префикс опций без "data-"
   * @param removeData Удалять ли опции из HTML (по умолчанию — true)
   */
  getElementCustomOptions<T extends {
    [key: string]: string
  }>(element: HTMLElement, prefix: string, removeData: boolean = true): HTMLOptions<T> {
    return SGPlugin.getElementOptions(element, prefix, removeData, this.optionAliases)
  }

  static getElementOptions<T extends {
    [key: string]: any
  }>(element: HTMLElement, prefix: string, removeData: boolean = true, aliases: {
    [key: string]: string
  } = {}): HTMLOptions<T> {
    // @ts-ignore
    const options: T = {}
    const attributes = element.attributes
    const toRemove: string[] = [];

    for (let i = 0; i < attributes.length; i++) {
      const { name, value } = attributes[i]
      if (!name.startsWith(`data-${prefix}-`)) {
        continue
      }
      const regEx = new RegExp(`^data-${prefix}-`)
      let optionName = name.replace(regEx, '')
      // Переименуем опции по алиасам
      if (aliases[optionName]) {
        optionName = aliases[optionName]
      }
      // @ts-ignore
      options[optionName] = value || true
      toRemove.push(name)
    }

    if (removeData) {
      toRemove.forEach(name => element.attributes.removeNamedItem(name))
    }

    return options
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Хелпер, достающий из указанного элемента
   * значения всех аттрибутов начинающихся с data-%name%, где
   * name — название плагина.
   * Если removeData = true — удаляем эти аттрибуты из кода
   * @see getElementCustomOptions
   * @param element Элемент из которого берём параметры
   * @param removeData Удалять ли опции из HTML (по умолчанию — true)
   */
  getElementOptions<T extends {
    [key: string]: string
  }>(element: HTMLElement, removeData: boolean = true): HTMLOptions<T> {
    const pluginName = this.name.toLowerCase()

    return this.getElementCustomOptions(element, pluginName, removeData)
  }
}

export default class SGPlugins {
  static plugins: SGPlugin[] = []
  static runOnMutation: number[] = []
  static timeout: number = 0

  static register(plugins: SGPluginContruct | Array<SGPluginContruct>) {
    if (!Array.isArray(plugins)) {
      plugins = [plugins]
    }

    plugins.forEach(plugin => {
      let instance: SGPlugin;
      if (typeof plugin === 'object') {
        instance = new SGPlugin()
        for (let name in plugin) {
          instance[name] = plugin[name]
        }
      } else {
        instance = new plugin()
      }
      SGPlugins.plugins.push(instance)
      const pluginKey = SGPlugins.plugins.length - 1
      if (instance.reactive) {
        SGPlugins.runOnMutation.push(pluginKey)
      }
    })

    return SGPlugins
  }

  protected static onMutation: MutationCallback = () => {
    SGPlugins.scheduleMutationCheck()
  }

  protected static scheduleMutationCheck = () => {
    if (SGPlugins.timeout) {
      clearTimeout(SGPlugins.timeout)
    }
    // @ts-ignore
    SGPlugins.timeout = setTimeout(SGPlugins.runAfterMutation, 500)
  }

  protected static runAfterMutation = () => {
    const elements = document.body.querySelectorAll('[data-plugins]')
    const pluginElements = {}

    elements.forEach(element => {
      const elementPlugins = element.getAttribute('data-plugins').split(' ')
      elementPlugins.forEach(pluginName => {
        if (pluginName === '' || !SGPlugins.runOnMutation.find(pluginKey => SGPlugins.plugins[pluginKey].name === pluginName)) {
          return
        }
        const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
        if (!plugin) {
          trace('SGPlugins', `Plugin ${pluginName} not found`, SGPlugins.plugins)
          return
        }
        if (plugin.mediaQuery && !window.matchMedia(plugin.mediaQuery).matches) {
          return
        }
        if (!pluginElements[pluginName]) {
          pluginElements[pluginName] = []
        }
        pluginElements[pluginName].push(element)
      })
    })

    Object.keys(pluginElements).forEach(pluginName => {
      const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
      if (!plugin) {
        trace('SGPlugins', `Plugin ${pluginName} not found`, SGPlugins.plugins)
        return
      }
      if (plugin.firstRun) {
        plugin.log('Initialized from mutation')
      }
      // noinspection JSIgnoredPromiseFromCall
      plugin.observerRun(pluginElements[pluginName])
    })
  }

  protected static onResize = () => {
    const elements = document.body.querySelectorAll('[data-plugins]')
    const pluginElements = {}

    elements.forEach(element => {
      const elementPlugins = element.getAttribute('data-plugins').split(' ')
      elementPlugins.forEach(pluginName => {
        if (pluginName === '') {
          return
        }
        const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
        if (!plugin || !plugin.mediaQuery) {
          return
        }
        if (!plugin.mediaQuery) {
          error(pluginName, 'Plugin has no mediaQuery!')
          return
        }
        if (!pluginElements[pluginName]) {
          pluginElements[pluginName] = []
        }
        pluginElements[pluginName].push(element)
      })
    })

    Object.keys(pluginElements).forEach(pluginName => {
      const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
      if (!plugin) {
        trace('SGPlugins', `Plugin ${pluginName} not found`, SGPlugins.plugins)
        return
      }
      if (!plugin.mediaQuery) {
        return
      }
      if (!plugin.attached && window.matchMedia(plugin.mediaQuery).matches) {
        plugin.log('Running after resize')
        const elements = plugin.filterElements(pluginElements[pluginName])
        if (elements.length > 0) {
          // noinspection JSIgnoredPromiseFromCall
          plugin.scheduleRun(pluginElements[pluginName])
          plugin.attached = true
        }
        return
      }

      if (plugin.attached && !window.matchMedia(plugin.mediaQuery).matches) {
        plugin.log('Detaching after resize')
        plugin.detach()
        plugin.attached = false
      }
    })
  }

  static init() {
    const elements = document.body.querySelectorAll('[data-plugins]')

    const pluginElements = {}

    elements.forEach(element => {
      const elementPlugins = element.getAttribute('data-plugins').split(' ')
      elementPlugins.forEach(pluginName => {
        if (pluginName === '') {
          return
        }
        const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
        if (!plugin) {
          trace('SGPlugins', `Plugin ${pluginName} not found`, SGPlugins.plugins)
          return
        }
        if (plugin.mediaQuery && !window.matchMedia(plugin.mediaQuery).matches) {
          return
        }
        if (!pluginElements[pluginName]) {
          pluginElements[pluginName] = []
        }
        pluginElements[pluginName].push(element)
      })
    })

    Object.keys(pluginElements).forEach(pluginName => {
      const plugin = SGPlugins.plugins.find(plugin => plugin.name === pluginName)
      if (!plugin) {
        trace('SGPlugins', `Plugin ${pluginName} not found`, SGPlugins.plugins)
        return
      }
      plugin.log('Initialized')
      const elements = plugin.filterElements(pluginElements[pluginName])
      if (elements.length > 0) {
        // noinspection JSIgnoredPromiseFromCall
        plugin.scheduleRun(pluginElements[pluginName])
        plugin.attached = true
      }
    })

    const mutationObserver = new MutationObserver(SGPlugins.onMutation)

    mutationObserver.observe(document.body, {
      attributes: false,
      childList: true,
      subtree: true
    })

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(SGPlugins.onResize)
      resizeObserver.observe(document.body)
    }
  }

  static loadCss(filename: string) {
    if (!document.querySelector(`link[href="${filename}"]`)) {
      const style = document.createElement('link')
      style.rel = 'stylesheet'
      style.href = filename
      document.head.append(style)
    }
  }

  static callPlugin(name: string, element: HTMLElement) {
    const plugin = SGPlugins.plugins.find(plugin => plugin.name === name)
    if (plugin) {
      plugin.observerRun([element])
    }
  }
}
